import { onMounted, onUnmounted, ref, watch, type Ref } from 'vue';

export function useViewportVisibility<T extends HTMLElement>(elements: Ref<T[]>) {
  const activeElement = ref<T | null>(null);

  // Calculate the visible area of an element in the viewport
  const calculateVisibleArea = (rect: DOMRect): number => {
    const viewportHeight = window.innerHeight;

    // Calculate the visible portion of the element
    const visibleTop = Math.max(0, rect.top);
    const visibleBottom = Math.min(viewportHeight, rect.bottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);

    // Calculate the total height of the element
    const totalHeight = rect.height;

    // Return the percentage of the element that is visible
    return visibleHeight / totalHeight;
  };

  // Find the element with the largest visible area
  const updateActiveElement = () => {
    // console.log('updateActiveElement called, elements:', elements.value.length);

    let bestCandidate: T | null = null;
    let bestScore = -1;

    elements.value.forEach((element) => {
      if (!element || typeof element.getBoundingClientRect !== 'function') {
        // console.log('Skipping invalid element');
        return;
      }

      const rect = element.getBoundingClientRect();
      if (!rect || rect.height === 0) {
        // console.log('No rect or zero height for element');
        return;
      }

      // Only consider elements that are at least partially in view
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        const visibleArea = calculateVisibleArea(rect);
        // console.log('Element visible area:', visibleArea, 'current best:', bestScore);
        if (visibleArea > bestScore) {
          bestScore = visibleArea;
          bestCandidate = element;
        }
      }
    });

    if (activeElement.value !== bestCandidate) {
      // const postId = bestCandidate
      //   ? (bestCandidate as HTMLElement).getAttribute('data-post-id')
      //   : null;
      // console.log('Active element changed:', postId || 'unknown');
      activeElement.value = bestCandidate;
    }
  };

  // Check if an element is currently active
  const isActive = (element: T) => {
    return activeElement.value === element;
  };

  // Setup event listeners
  const setupListeners = () => {
    window.addEventListener('scroll', updateActiveElement, { passive: true });
    window.addEventListener('resize', updateActiveElement, { passive: true });
  };

  // Remove event listeners
  const cleanupListeners = () => {
    window.removeEventListener('scroll', updateActiveElement);
    window.removeEventListener('resize', updateActiveElement);
  };

  watch(elements, () => updateActiveElement());

  onMounted(() => {
    setupListeners();
  });

  onUnmounted(() => {
    cleanupListeners();
  });

  return {
    activeElement,
    isActive,
    updateActiveElement,
    setupListeners,
    cleanupListeners,
  };
}
